import { useState, useRef, useEffect, useMemo } from 'react'
import { Columns3, Check, ChevronUp, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useColumnPrefsStore } from '../stores/columnPrefsStore'

const ALL_COLUMNS = [
  { id: 'order_id', label: 'Ordrenr', required: true },
  { id: 'customer_name', label: 'Kunde', required: true },
  { id: 'reference', label: 'Merking' },
  { id: 'app_status', label: 'Status' },
  { id: 'employee_id', label: 'Ansatt' },
  { id: 'order_date', label: 'Dato' },
  { id: 'customer_phone', label: 'Tlf' },
  { id: 'email', label: 'E-post' },
  { id: 'delivery_address', label: 'Adresse' },
  { id: 'order_template_id', label: 'Ordremal' },
  { id: 'invoice_no', label: 'Fakturanr' },
  { id: 'delivery_date', label: 'Leveringsdato' },
  { id: 'your_ref', label: 'Kundens ref' },
  { id: 'info', label: 'Ordreinfo' },
  { id: 'customer_id', label: 'Kundenr' },
  { id: 'line_count', label: 'Antall linjer' },
  { id: 'order_total', label: 'Total eks. mva' },
  { id: 'delivery_name', label: 'Leveringsnavn' },
]

const columnLabelMap = new Map(ALL_COLUMNS.map((c) => [c.id, c.label]))
const requiredIds = new Set(ALL_COLUMNS.filter((c) => 'required' in c && c.required).map((c) => c.id))

export function ColumnVisibilityDropdown() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const visibleColumns = useColumnPrefsStore((s) => s.visibleColumns)
  const toggleColumn = useColumnPrefsStore((s) => s.toggleColumn)
  const moveColumn = useColumnPrefsStore((s) => s.moveColumn)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Visible columns first (in order), then hidden ones
  const sortedColumns = useMemo(() => {
    const hiddenIds = ALL_COLUMNS
      .filter((c) => !visibleColumns.includes(c.id))
      .map((c) => c.id)
    return [...visibleColumns, ...hiddenIds]
  }, [visibleColumns])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[12px] px-2.5 py-1.5 rounded-md border border-gray-200 text-gray-600 bg-white hover:border-gray-300 transition-colors"
        title="Velg kolonner"
      >
        <Columns3 size={13} />
        Kolonner
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 max-h-80 overflow-y-auto">
          {sortedColumns.map((colId, idx) => {
            const label = columnLabelMap.get(colId) ?? colId
            const isVisible = visibleColumns.includes(colId)
            const isRequired = requiredIds.has(colId)
            const visibleIdx = visibleColumns.indexOf(colId)
            const canMoveUp = isVisible && visibleIdx > 0
            const canMoveDown = isVisible && visibleIdx < visibleColumns.length - 1

            // Separator between visible and hidden
            const isFirstHidden = !isVisible && idx === visibleColumns.length

            return (
              <div key={colId}>
                {isFirstHidden && (
                  <div className="border-t border-gray-100 my-1" />
                )}
                <div
                  className={cn(
                    'flex items-center gap-1.5 w-full px-2 py-1 text-[12px]',
                    isRequired ? 'opacity-50' : 'hover:bg-gray-50'
                  )}
                >
                  {/* Checkbox */}
                  <button
                    onClick={() => !isRequired && toggleColumn(colId)}
                    disabled={isRequired}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    <div
                      className={cn(
                        'w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0',
                        isVisible
                          ? 'bg-emerald-500 border-emerald-500'
                          : 'border-gray-300'
                      )}
                    >
                      {isVisible && <Check size={10} className="text-white" />}
                    </div>
                    <span className="truncate">{label}</span>
                  </button>

                  {/* Move arrows — only for visible columns */}
                  {isVisible && (
                    <span className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => moveColumn(colId, 'up')}
                        disabled={!canMoveUp}
                        className={cn(
                          'p-0.5 rounded',
                          canMoveUp ? 'hover:bg-gray-200 text-gray-500' : 'text-gray-200'
                        )}
                      >
                        <ChevronUp size={12} />
                      </button>
                      <button
                        onClick={() => moveColumn(colId, 'down')}
                        disabled={!canMoveDown}
                        className={cn(
                          'p-0.5 rounded',
                          canMoveDown ? 'hover:bg-gray-200 text-gray-500' : 'text-gray-200'
                        )}
                      >
                        <ChevronDown size={12} />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
